/**
 * Модель расписания правила (ТЗ Part 2, разделы 3 и 4).
 *
 * Внутри редактора окно живёт как { id, weekday, startTime, endTime }, где id —
 * локальный ключ для React и для привязки ошибок. Серверные window_id,
 * schedule_version и effective_at read-only: фронт их не генерирует.
 */

export const ACTIVATION_ALWAYS = 'always';
export const ACTIVATION_SCHEDULED = 'scheduled';

export const MINUTES_IN_DAY = 1440;
export const MINUTES_IN_WEEK = MINUTES_IN_DAY * 7;

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** 'HH:MM' → минуты от полуночи; всё остальное → null. */
export const parseTime = (value) => {
  const match = typeof value === 'string' ? value.match(TIME_PATTERN) : null;
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

let windowSequence = 0;

export const createWindow = (over = {}) => {
  windowSequence += 1;
  return { id: `w${windowSequence}`, weekday: 0, startTime: '08:00', endTime: '18:00', ...over };
};

/**
 * Окно на недельной оси минут [0..10080).
 *
 * Cross-midnight хранится ОДНИМ окном (ТЗ 4.2): Пн 23:00–10:45 значит
 * Пн 23:00 → Вт 10:45. Если окно перешагивает конец недели, оно режется на два
 * отрезка — иначе «Вс 23:00 → Пн 10:45» ложно не пересекается ни с чем.
 */
export const expandWindow = (window) => {
  const startMinutes = parseTime(window.startTime);
  const endMinutes = parseTime(window.endTime);
  if (startMinutes === null || endMinutes === null || startMinutes === endMinutes) return [];

  const start = Number(window.weekday) * MINUTES_IN_DAY + startMinutes;
  const duration = endMinutes > startMinutes
    ? endMinutes - startMinutes
    : MINUTES_IN_DAY - startMinutes + endMinutes;
  const end = start + duration;

  if (end <= MINUTES_IN_WEEK) return [{ start, end }];
  return [
    { start, end: MINUTES_IN_WEEK },
    { start: 0, end: end - MINUTES_IN_WEEK },
  ];
};

// Отрезки полуоткрытые [start, end), поэтому 08:00–12:00 и 12:00–16:00 смежны, а не пересекаются.
const segmentsOverlap = (a, b) => a.start < b.end && b.start < a.end;

const windowsOverlap = (a, b) =>
  expandWindow(a).some((left) => expandWindow(b).some((right) => segmentsOverlap(left, right)));

export const validateActivation = (model) => {
  const result = { valid: true, timezoneError: null, generalError: null, windowErrors: {} };
  if (model.mode !== ACTIVATION_SCHEDULED) return result;

  if (!model.timezone) {
    result.timezoneError = 'Выберите часовой пояс';
  }

  const windows = Array.isArray(model.windows) ? model.windows : [];
  if (windows.length === 0) {
    result.generalError = 'Добавьте хотя бы один интервал';
  }

  windows.forEach((window) => {
    const startMinutes = parseTime(window.startTime);
    const endMinutes = parseTime(window.endTime);
    if (startMinutes === null || endMinutes === null) {
      result.windowErrors[window.id] = 'Укажите время в формате ЧЧ:ММ';
    } else if (startMinutes === endMinutes) {
      result.windowErrors[window.id] = 'Начало и окончание не могут совпадать';
    }
  });

  windows.forEach((left, index) => {
    windows.slice(index + 1).forEach((right) => {
      if (result.windowErrors[left.id] || result.windowErrors[right.id]) return;
      if (!windowsOverlap(left, right)) return;
      result.windowErrors[left.id] = 'Интервал пересекается с другим';
      result.windowErrors[right.id] = 'Интервал пересекается с другим';
    });
  });

  result.valid = !result.timezoneError
    && !result.generalError
    && Object.keys(result.windowErrors).length === 0;
  return result;
};

export const fromActivationDto = (activation) => {
  const mode = activation?.mode === ACTIVATION_SCHEDULED ? ACTIVATION_SCHEDULED : ACTIVATION_ALWAYS;
  const rawWindows = Array.isArray(activation?.windows) ? activation.windows : [];
  return {
    mode,
    timezone: activation?.timezone || '',
    windows: rawWindows.map((window) => createWindow({
      weekday: Number(window.weekday) || 0,
      startTime: window.start_time || '',
      endTime: window.end_time || '',
    })),
  };
};

export const toActivationDto = (model) => {
  if (model.mode !== ACTIVATION_SCHEDULED) return { mode: ACTIVATION_ALWAYS };
  return {
    mode: ACTIVATION_SCHEDULED,
    timezone: model.timezone,
    windows: model.windows.map((window) => ({
      weekday: Number(window.weekday),
      start_time: window.startTime,
      end_time: window.endTime,
    })),
  };
};
